// Sign-in expiry recovery (NP-2).
//
// When an API request fails with 401 (the 24-hour access cookie expired),
// ask the backend for a new one via POST /auth/refresh (30-day refresh
// cookie) and retry the original request ONCE. Guarantees:
//   • never retries the auth endpoints themselves (refresh, login,
//     register, logout, Google session) — so a wrong password stays a
//     wrong password and the refresh call can never trigger itself;
//   • a request is retried at most once (`_authRetried` flag);
//   • concurrent 401s share a single refresh call;
//   • if the refresh fails, or the retried request is still 401, the
//     error is marked `sessionExpired` and `onExpired()` is called.
//
// Pure: the HTTP client is injected, so this is unit-testable.

const AUTH_ENDPOINT = /\/auth\/(refresh|login|register|logout|session)\/?(\?|$)/;

export function isAuthRefreshExcluded(config) {
  if (!config) return true;
  if (config.skipAuthRefresh) return true;
  return AUTH_ENDPOINT.test(config.url || "");
}

export function createAuthRefreshHandler({ retry, refresh, onExpired }) {
  let inFlight = null;
  const refreshOnce = () => {
    if (!inFlight) {
      inFlight = Promise.resolve()
        .then(refresh)
        .finally(() => {
          inFlight = null;
        });
    }
    return inFlight;
  };
  const expire = (error) => {
    error.sessionExpired = true;
    if (onExpired) onExpired();
    return Promise.reject(error);
  };

  return async function onRejected(error) {
    const config = error && error.config;
    const status = error && error.response && error.response.status;
    if (status !== 401 || isAuthRefreshExcluded(config)) throw error;
    if (config._authRetried) return expire(error);
    try {
      await refreshOnce();
    } catch {
      return expire(error);
    }
    return retry({ ...config, _authRetried: true });
  };
}
